import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import App from './App';
import { AuthCallback } from './auth/AuthCallback';
import './i18n';
import './styles.css';
import './theme/chemicalThemes.css';
import { getAccessToken, reauthenticate } from './auth/authClient';
import { configureApiAuth, reportClientError } from '@peruselab/api-client';
import { takeBackServerHead } from './features/public/CatalogueMeta';

configureApiAuth({
  getAccessToken,
  onUnauthorized: () => {
    // #885: a signed-out visitor browsing public pages never had a session to renew -- a 401 is
    // shown where it happened, not turned into a forced sign-in redirect.
    if (!getAccessToken()) return;
    reauthenticate(window.location.pathname + window.location.search + window.location.hash).catch((error: unknown) => {
      const message = error instanceof Error ? error.message : String(error);
      console.error('[api] reauth redirect failed:', error);
      reportClientError('reauth_redirect_failed', message);
    });
  }
});

const isAuthCallback = window.location.pathname === '/auth/callback';

// Hosts that fall back to the SPA for /help still reach the public static manual.
if (window.location.pathname === '/help') {
  window.location.replace(`/help/${window.location.search}${window.location.hash}`);
} else {
  takeBackServerHead();
  createRoot(document.getElementById('root')!).render(
    <StrictMode>{isAuthCallback ? <AuthCallback /> : <App />}</StrictMode>
  );
}

// The retired service worker cached authenticated API GET responses. Remove any existing
// registration and its historical chem-draw-poc-* caches; offline behavior can be reintroduced
// later with an asset-only strategy and dedicated tests.
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    void navigator.serviceWorker
      .getRegistrations()
      .then((registrations) =>
        Promise.all(
          registrations
            .filter((registration) =>
              [registration.active, registration.waiting, registration.installing].some(
                (worker) => worker && new URL(worker.scriptURL).pathname === '/sw.js'
              )
            )
            .map((registration) => registration.unregister())
        )
      );
    if ('caches' in window) {
      void caches
        .keys()
        .then((keys) =>
          Promise.all(keys.filter((key) => key.startsWith('chem-draw-poc-')).map((key) => caches.delete(key)))
        );
    }
  });
}
